import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { AppAvailability, DayPlanModule, ModuleTemplate, PopDataType, StudyService } from '@nx-superprep/backend/firebase';
import { ModalService } from '@nx-superprep/shared';
import { DateOnly, log } from '@nx-superprep/utils';
import { LessonTile } from './lesson-tile';
import { LibraryAction, StudyAction } from './study-types'

@Injectable({ providedIn: 'root' })
export class LessonTileService {
  private readonly router = inject(Router);
  private readonly studyService = inject(StudyService);
  private readonly modalService = inject(ModalService);

  createLessonTile(m: DayPlanModule | LessonTile | ModuleTemplate, options: {
    submitted: boolean,
    progress?: number;
    finished?: () => boolean;
    subscribed?: () => boolean;
    selectAction?: () => Promise<boolean>;
    addAction?: () => Promise<boolean>;
    delAction?: () => Promise<boolean>;
    studyAction?: () => Promise<boolean>;
    tryAction?: () => Promise<boolean>;
    detailAction?: () => Promise<boolean>;
    onHide?: (reason: string) => void;
  }): LessonTile {
    const _performAction = (id: string | undefined, action: StudyAction | LibraryAction) => () => {
      if (!id) { return Promise.resolve(false); }
      if (action === 'add' || action === 'del') {
        return this.studyService?.[action === 'add' ? 'includeModule' : 'removeModule'](id)
          .then((success) => {
            log.debug(`${success ? '' : 'not '}${action === 'add' ? 'added' : 'removed'} ${_m.id}`)
            return success;
          });
      } else {
        if (!id || !this.modalService) { return Promise.resolve(false); }
        this.modalService.openModal(action, id);
        return Promise.resolve(true);
      }
    };

    const { submitted, progress, finished, subscribed, selectAction, addAction, delAction, studyAction, tryAction, detailAction } = options;

    const _m = m instanceof DayPlanModule ? {
      id: m.moduleId,
      image: m.moduleIcon,
      title: m.moduleTitle,
      description: m.moduleTitle,
      estimated: m.revisedCompletionTime,
      progress: m.progress,
      finished: m.isFinished,
      expectedStudyDate: m.expectedStudyDate,
      assigned: m.isAssigned,
    } : m;

    log.trace(`Creating lesson tile for module ${_m.id}`);

    const _result: LessonTile = {
      ..._m,
      id: _m.id,
      image: 'image' in _m ? _m.image : undefined,
      selected: 'selected' in _m ? _m.selected : undefined,
      color: 'color' in _m ? _m.color : undefined,
      background: 'background' in _m ? _m.background : undefined,
      activeDate: 'activeDate' in _m ? _m.activeDate : undefined,
      itemId: 'itemId' in _m ? _m.itemId : _m.id,
      dataType: 'dataType' in _m ? _m.dataType : PopDataType.ModuleTemplate,
      iconName: 'iconName' in _m ? _m.iconName : undefined,
      iconUrl: 'iconUrl' in _m ? _m.iconUrl : undefined,
      title: _m.title,
      representationColor: 'representationColor' in _m ?  _m.representationColor || '#000000' : '#000000',
      description: _m.description,
      subjects: 'subjects' in _m ? _m.subjects : [],
      estimated: 'estimated' in _m ? _m.estimated : 0,
      subject: ('subjects' in _m && _m.subjects.length > 0 ? _m.subjects[0].title ?? '' : ''),
      stats: 'stats' in _m ? _m.stats ?? [] : [],
      terms: 'terms' in _m ? _m.terms ?? [] : [],
      progress: 'progress' in m ? m.progress : Number(progress) || 0,
      expectedStudyDate: 'expectedStudyDate' in _m ? (_m.expectedStudyDate instanceof Date ? _m.expectedStudyDate : _m.expectedStudyDate?.toDate()) : undefined,
      isNew: () => 'lastUpdateDate' in _m && new DateOnly(_m.lastUpdateDate).isAfter(new DateOnly().addMonths(-1)),
      isAssigned: () => 'assigned' in _m && !!_m.assigned,
      isProduction: () => 'appAvailability' in _m && (_m.appAvailability as AppAvailability).LT_PROD_WEB,
      isValid: () => 'cardCount' in _m ? (Number(_m.cardCount) || 0) > 0 : true,
      finished: finished ? finished() : ('finished' in _m ? !!_m.finished : !!this.studyService?.isFinishedModule(_m.id)),
      subscribed: subscribed ? subscribed() : () => this.studyService?.isIncludedModule(_m.id) ?? false,
      action: (action: string) => {
        return ((id, submitted) => {
          if (submitted) { return Promise.reject(`already submitted ${_m.id}`); }
          submitted = true;
          log.trace(action, _m.id);
          try {
            switch (action) {
              case 'select': return Promise.resolve(selectAction ? selectAction() : false);
              case 'add': return Promise.resolve(addAction ? addAction() : _performAction(id, 'add')() ?? false);
              case 'del': return Promise.resolve(delAction ? delAction() : _performAction(id, 'del')() ?? false);
              case 'study': return Promise.resolve(studyAction ? studyAction() : _performAction(id, 'study')() ?? false);
              case 'try': return Promise.resolve(tryAction ? tryAction() : _performAction(id, 'practice')() ?? false);
              case 'quiz': return Promise.resolve(tryAction ? tryAction() : _performAction(id, 'quiz')() ?? false);
              case 'detail': return Promise.resolve(detailAction ? detailAction() : true);
              default: return Promise.resolve(true);
            }
          } catch (error) {
            log.error(`Action ${action} failed for module ${_m.id}:`, error);
            return Promise.reject(error);
          } finally {
            submitted = false;
          }
        })(_m.id, submitted)
      },
    };

    log.trace(`Expected study date for ${_result.id}: ${_result.expectedStudyDate??'today'}`);
    return _result as unknown as LessonTile;
  }
}
